import React, { useEffect, useState } from "react";
import { Play, Clock, Youtube, ExternalLink, Filter, BookOpen, Shield, Heart, Sparkles, ClipboardList, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { Tx } from "../context/AutoTranslateContext";
import RuqyahProgrammesSection from "../components/RuqyahProgrammesSection";
import OnlineRuqyahGroupCard from "../components/OnlineRuqyahGroupCard";
import ShareOnWhatsApp from "../components/ShareOnWhatsApp";

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}m ${s.toString().padStart(2, "0")}s`;
};

// Sacred sources of Al-Ruqyah Al-Shar'iyyah — the foundational Quranic chapters & verses
// recited for spiritual protection, healing from Sihr, Al-Ayn, and jinn affliction.
const SACRED_SOURCES = [
  {
    id: "fatiha",
    icon: BookOpen,
    name_en: "Surah Al-Fatiha",
    name_ar: "سُورَةُ الفَاتِحَة",
    subtitle: "The Opening · 1:1–7",
    note: "Called Ash-Shifa (The Cure). The Prophet ﷺ approved its recitation over a man bitten by a scorpion — narrated in Sahih al-Bukhari.",
    img: "https://images.unsplash.com/photo-1609599006353-e629aaabfeae?w=900&q=80&auto=format&fit=crop",
    accent: "#2C5545",
  },
  {
    id: "kursi",
    icon: Shield,
    name_en: "Ayat al-Kursi",
    name_ar: "آيَةُ الكُرْسِيّ",
    subtitle: "The Throne Verse · 2:255",
    note: "Whoever recites it after every fard prayer — nothing prevents him from Paradise except death (Sahih al-Jami'). The greatest verse of the Qur'an.",
    img: "https://images.unsplash.com/photo-1576764402988-7143f9cca90a?w=900&q=80&auto=format&fit=crop",
    accent: "#B68D40",
  },
  {
    id: "muawwidhatan",
    icon: Sparkles,
    name_en: "Al-Mu'awwidhatān",
    name_ar: "المُعَوِّذَتَان",
    subtitle: "Al-Falaq + An-Nās · 113 & 114",
    note: "The Prophet ﷺ would recite them, blow into his hands and wipe his body before sleep (Bukhari). Revealed to ward off Sihr and Al-Ayn.",
    img: "https://images.unsplash.com/photo-1597505495109-7fc35bb64d8e?w=900&q=80&auto=format&fit=crop",
    accent: "#8C3A3A",
  },
  {
    id: "baqarah",
    icon: Heart,
    name_en: "Last 2 Verses of Al-Baqarah",
    name_ar: "خَوَاتِيمُ البَقَرَة",
    subtitle: "Āmana ar-Rasūl · 2:285–286",
    note: "Whoever recites them at night, they will suffice him (Sahih al-Bukhari). A shield against Shayṭān and a comprehensive du'ā of mercy.",
    img: "https://images.unsplash.com/photo-1542816417-0983c9c9ad53?w=900&q=80&auto=format&fit=crop",
    accent: "#2C5545",
  },
];

const Ruqyah = () => {
  const [data, setData] = useState(null);
  const [audio, setAudio] = useState([]);
  const [filter, setFilter] = useState("all");
  const [playing, setPlaying] = useState(null);
  const { lang, t } = useLanguage();

  useEffect(() => {
    api.get("/ruqyah/videos").then((r) => setData(r.data)).catch(() => {});
    api.get("/ruqyah/audio").then((r) => setAudio(r.data.recitations || [])).catch(() => {});
  }, []);

  if (!data) {
    return <div className="max-w-5xl mx-auto py-20 px-4 text-center text-mute">Loading…</div>;
  }

  const { videos, categories, channel_url } = data;
  const filterOptions = ["all", ...Object.keys(categories)];
  const visible = filter === "all" ? videos : videos.filter((v) => v.category === filter);

  return (
    <div data-testid="ruqyah-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-20">
      {/* Calligraphic hero banner: Al Ruqyah Al Shar'iyyah */}
      <section
        data-testid="ruqyah-hero-banner"
        className="relative overflow-hidden rounded-3xl mb-12 sm:mb-16"
        style={{ minHeight: "340px" }}
      >
        <img
          src="https://images.unsplash.com/photo-1609599006353-e629aaabfeae?w=1800&q=85&auto=format&fit=crop"
          alt="Al-Ruqyah Al-Shar'iyyah — open Mushaf of the Holy Qur'an"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(115deg, rgba(44,85,69,0.92) 0%, rgba(44,85,69,0.78) 45%, rgba(140,58,58,0.55) 100%)",
          }}
        />
        {/* Decorative arabesque corners */}
        <div className="absolute top-6 left-6 right-6 flex justify-between text-gold/40 text-3xl font-bold pointer-events-none">
          <span>﴿</span>
          <span>﴾</span>
        </div>
        <div className="relative z-10 px-6 sm:px-12 py-14 sm:py-20 text-center">
          <p className="text-gold/90 text-xs tracking-[0.35em] uppercase mb-4">
            Bismillāhi-r-Raḥmāni-r-Raḥīm
          </p>
          <h2
            data-testid="ruqyah-hero-arabic"
            className="arabic text-sand text-5xl sm:text-7xl lg:text-8xl leading-tight drop-shadow-lg"
            dir="rtl"
          >
            الرُّقْيَةُ الشَّرْعِيَّة
          </h2>
          <div className="flex items-center justify-center gap-3 my-5">
            <span className="h-px w-12 bg-gold/60" />
            <span className="text-gold text-sm tracking-[0.3em] uppercase">Al-Ruqyah Al-Shar'iyyah</span>
            <span className="h-px w-12 bg-gold/60" />
          </div>
          <p className="text-sand/85 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed italic">
            <Tx>"And We send down of the Qur'an that which is healing and mercy for the believers."</Tx>
            <span className="block mt-2 text-gold/80 text-xs not-italic tracking-wider"><Tx>— Surah Al-Isrā' 17:82</Tx></span>
          </p>
        </div>
      </section>

      <div className="mb-10 max-w-3xl">
        <div className="inline-flex items-center gap-2 bg-clay/10 text-clay rounded-full px-4 py-1.5 text-xs mb-5">
          <Youtube className="w-3.5 h-3.5" />
          <Tx>Live recordings · Raqi Amar Faragalla</Tx>
        </div>
        <p className="arabic text-gold text-2xl mb-3">مكتبة الرقية الشرعية</p>
        <h1 data-testid="ruqyah-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-forest font-bold tracking-tight mb-5">
          <Tx>Listen to authentic Ruqyah recordings.</Tx>
        </h1>
        <p className="text-mute leading-relaxed text-lg mb-5">
          <Tx>Curated live Ruqyah sessions from our clinic — for Black Magic (Sihr), Evil Eye (Al-Ayn), jinn affliction, and general healing. Listen in the app or watch on YouTube.</Tx>
        </p>
        <a
          href={channel_url}
          target="_blank"
          rel="noreferrer"
          data-testid="channel-link"
          className="inline-flex items-center gap-2 text-clay hover:text-forest font-medium text-sm transition"
        >
          <Youtube className="w-4 h-4" />
          <Tx>Visit the full channel @ammargebril</Tx>
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>

      {/* Sacred Sources — Quranic foundations of Al-Ruqyah Al-Shar'iyyah */}
      <section data-testid="sacred-sources" className="mb-14 sm:mb-16">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <p className="arabic text-gold text-xl mb-1">المَصَادِرُ القُرْآنِيَّة</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-forest font-bold">
              <Tx>Sacred Sources of Ruqyah</Tx>
            </h2>
            <p className="text-mute text-sm mt-2 max-w-2xl">
              <Tx>The Qur'anic chapters and verses the Prophet ﷺ recited for spiritual healing and protection. Recite them with sincere intention (niyyah) and trust in Allah alone.</Tx>
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SACRED_SOURCES.map((src) => {
            const Icon = src.icon;
            return (
              <article
                key={src.id}
                data-testid={`sacred-source-${src.id}`}
                className="group relative rounded-2xl overflow-hidden bg-cream lift-on-hover flex flex-col"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={src.img}
                    alt={`${src.name_en} — Al-Ruqyah Al-Shar'iyyah`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.55) 70%, ${src.accent}E6 100%)`,
                    }}
                  />
                  <div className="absolute top-3 left-3 w-10 h-10 rounded-full bg-sand/95 flex items-center justify-center shadow-md">
                    <Icon className="w-5 h-5" style={{ color: src.accent }} />
                  </div>
                  <div className="absolute bottom-3 left-3 right-3 text-sand">
                    <p className="arabic text-2xl leading-tight drop-shadow" dir="rtl">
                      {src.name_ar}
                    </p>
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <p className="text-[10px] uppercase tracking-widest text-gold mb-1">
                    <Tx>{src.subtitle}</Tx>
                  </p>
                  <h3 className="font-heading text-lg text-forest font-semibold mb-2 leading-snug">
                    {lang === "ar" ? src.name_ar : <Tx>{src.name_en}</Tx>}
                  </h3>
                  <p className="text-xs text-mute leading-relaxed flex-1"><Tx>{src.note}</Tx></p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Weekly Online Ruqyah Group — women 4 PM, men 6 PM (UK) — £25 */}
      <OnlineRuqyahGroupCard />

      {/* Download Ruqyah Programme PDF (recovered from old website) */}
      <section data-testid="ruqyah-programme-download" className="mb-12 rounded-3xl overflow-hidden border border-gold/40 bg-gradient-to-br from-forest to-[#1F4030] text-sand">
        <div className="p-7 sm:p-10 grid md:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3"><Tx>Free Download · PDF</Tx></p>
            <h3 className="font-heading text-2xl sm:text-3xl font-semibold mb-3">
              <Tx>Ruqyah Programme for Patients</Tx>
            </h3>
            <p className="text-sand/85 text-sm leading-relaxed mb-2">
              <Tx>The complete daily, weekly, and monthly self-treatment regimen used by our clinic — including the famous 7-Day Ruqyah Detox Programme by Sheikh 'Ādil ibn Ṭāhir al-Muqbil.</Tx>
            </p>
            <p className="text-sand/70 text-xs leading-relaxed">
              <Tx>Pre-conditions · Daily adhkār · Qur'ān recitation · Sūrah al-Baqarah · 7-day detox preparation with Zam Zam water, olive oil, honey & black seed.</Tx>
            </p>
          </div>
          <a
            data-testid="download-ruqyah-programme-btn"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/ruqyah/programme.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-[#A07A2F] text-ink font-semibold px-7 py-4 rounded-full transition shadow-lg whitespace-nowrap"
          >
            <BookOpen className="w-5 h-5" /> <Tx>Download PDF</Tx>
          </a>
        </div>
      </section>

      {/* Diagnostic Questionnaire CTA — direct to WhatsApp */}
      <section
        data-testid="diagnostic-cta-card"
        className="mb-16 rounded-3xl overflow-hidden border border-gold/30 bg-gradient-to-br from-cream to-sand/70 p-7 sm:p-10 grid md:grid-cols-[1fr_auto] gap-6 items-center"
      >
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-clay font-semibold mb-2">
            {t("diagnostic_cta_pill")}
          </p>
          <h3 className="font-heading text-2xl sm:text-3xl text-forest font-semibold mb-3 leading-snug">
            {t("diagnostic_cta_title")}
          </h3>
          <p className="text-sm text-mute leading-relaxed max-w-2xl">{t("diagnostic_cta_subtitle")}</p>
        </div>
        <Link
          data-testid="start-diagnostic-btn"
          to="/ruqyah/diagnostic"
          className="inline-flex items-center justify-center gap-2 bg-forest hover:bg-[#1F4030] text-sand font-semibold px-7 py-4 rounded-full transition shadow-lg whitespace-nowrap"
        >
          <ClipboardList className="w-5 h-5" /> {t("diagnostic_cta_btn")}
        </Link>
      </section>

      {/* Additional Ruqyah programmes (House Cleaning, Womb Steam, Vomiting) */}
      <RuqyahProgrammesSection />

      {/* Audio Recitations — recovered from old IslamicCureAndCare.co.uk site, by 5 famous Qaris */}
      {audio.length > 0 && (
        <section data-testid="ruqyah-audio-section" className="mb-16 bg-cream/50 rounded-3xl p-6 sm:p-10 border border-gold/30">
          <div className="text-center mb-8">
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-2">Tilāwāt al-Ruqyah · تلاوات الرقية</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-forest font-semibold">
              <Tx>Healing Ruqyah Recitations</Tx>
            </h2>
            <p className="text-sm text-mute mt-3 max-w-2xl mx-auto">
              <Tx>Listen to the complete authentic Ruqyah Shar'iyyah recited by the world's most beloved Qaris. Use daily for protection from 'Ayn (evil eye), Sihr (black magic), and Jinn-related afflictions.</Tx>
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {audio.map((rec) => (
              <article
                key={rec.id}
                data-testid={`ruqyah-audio-${rec.id}`}
                className="bg-white rounded-2xl p-5 border border-gold/20 shadow-sm hover:shadow-md transition"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1">
                    <p className="arabic text-base text-gold mb-0.5" dir="rtl">{rec.arabic_qari}</p>
                    <h3 className="font-heading text-lg text-forest font-semibold leading-snug">
                      {lang === "ar" ? rec.arabic_qari : <Tx>{rec.qari}</Tx>}
                    </h3>
                  </div>
                  <span className="text-[10px] bg-forest/10 text-forest px-2 py-1 rounded-full whitespace-nowrap">
                    ~{rec.duration_minutes} <Tx>min</Tx>
                  </span>
                </div>
                <p className="text-xs text-mute leading-relaxed mb-3"><Tx>{rec.description}</Tx></p>
                <audio
                  data-testid={`ruqyah-audio-player-${rec.id}`}
                  controls
                  preload="none"
                  className="w-full mt-2"
                  style={{ borderRadius: "8px" }}
                >
                  <source src={`${process.env.REACT_APP_BACKEND_URL}/api/ruqyah/audio/${rec.id}/file`} type="audio/mpeg" />
                  Your browser does not support audio playback.
                </audio>
                <div className="mt-3 flex justify-end">
                  <ShareOnWhatsApp
                    title={`${rec.qari} — Ruqyah Recitation`}
                    url={`${window.location.origin}/ruqyah#audio-${rec.id}`}
                  />
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* Filter pills */}
      <div data-testid="ruqyah-filters" className="flex flex-wrap items-center gap-2 mb-8">
        <Filter className="w-4 h-4 text-forest mr-1" />
        {filterOptions.map((key) => {
          const meta = key === "all"
            ? { label_en: `All (${videos.length})`, label_ar: `الكل (${videos.length})`, color: "#2C5545" }
            : categories[key];
          const active = filter === key;
          return (
            <button
              key={key}
              data-testid={`filter-${key}`}
              onClick={() => setFilter(key)}
              className={`text-xs sm:text-sm px-4 py-2 rounded-full transition border ${
                active
                  ? "text-sand border-transparent"
                  : "text-forest bg-cream border-forest/10 hover:bg-white"
              }`}
              style={active ? { backgroundColor: meta.color } : {}}
            >
              {lang === "ar" ? meta.label_ar : <Tx>{meta.label_en}</Tx>}
            </button>
          );
        })}
      </div>

      {/* Video grid */}
      <div className="grid md:grid-cols-2 gap-6 stagger">
        {visible.map((v) => {
          const meta = categories[v.category] || {};
          const isPlaying = playing === v.id;
          return (
            <article
              key={v.id}
              data-testid={`ruqyah-card-${v.id}`}
              className="clay-surface rounded-3xl overflow-hidden lift-on-hover flex flex-col"
            >
              {/* Thumbnail / embed */}
              <div className="relative aspect-video bg-forest/10">
                {isPlaying ? (
                  <iframe
                    data-testid={`ruqyah-embed-${v.id}`}
                    src={`https://www.youtube.com/embed/${v.id}?autoplay=1&rel=0`}
                    title={v.title_en}
                    className="absolute inset-0 w-full h-full"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <button
                    data-testid={`play-button-${v.id}`}
                    onClick={() => setPlaying(v.id)}
                    className="absolute inset-0 group"
                    aria-label={`Play ${v.title_en}`}
                  >
                    <img
                      src={`https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`}
                      alt={v.title_en}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-sand/90 group-hover:bg-gold text-forest group-hover:text-sand flex items-center justify-center transition-all group-hover:scale-110 shadow-2xl">
                        <Play className="w-7 h-7 ml-1" fill="currentColor" />
                      </div>
                    </div>
                    <div className="absolute top-3 right-3 bg-black/70 text-sand text-xs px-2 py-1 rounded-full flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDuration(v.duration_sec)}
                    </div>
                  </button>
                )}
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <span
                    className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full whitespace-nowrap"
                    style={{ backgroundColor: `${meta.color}15`, color: meta.color }}
                  >
                    {lang === "ar" ? meta.label_ar : <Tx>{meta.label_en}</Tx>}
                  </span>
                </div>
                <p className="arabic text-gold text-base mb-2">{v.title_ar}</p>
                <h3 className="font-heading text-xl text-forest font-semibold leading-snug mb-3">
                  {lang === "ar" ? v.title_ar : <Tx>{v.title_en}</Tx>}
                </h3>
                <p className="text-sm text-mute leading-relaxed mb-4 flex-1"><Tx>{v.description}</Tx></p>
                <div className="flex items-center gap-3 pt-3 border-t border-forest/10 text-xs">
                  {!isPlaying && (
                    <button
                      data-testid={`listen-${v.id}`}
                      onClick={() => setPlaying(v.id)}
                      className="inline-flex items-center gap-1.5 text-forest hover:text-gold font-medium transition"
                    >
                      <Play className="w-3.5 h-3.5" fill="currentColor" />
                      <Tx>Listen here</Tx>
                    </button>
                  )}
                  <a
                    href={`https://youtube.com/watch?v=${v.id}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`youtube-${v.id}`}
                    className="inline-flex items-center gap-1.5 text-mute hover:text-clay transition ml-auto"
                  >
                    <Youtube className="w-3.5 h-3.5" />
                    <Tx>Open in YouTube</Tx>
                  </a>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {visible.length === 0 && (
        <div className="text-center py-20 text-mute"><Tx>No videos in this category yet.</Tx></div>
      )}

      {/* Disclaimer */}
      <div className="mt-12 rounded-3xl bg-cream/60 p-8 sm:p-10 text-center">
        <p className="text-sm text-mute leading-relaxed max-w-3xl mx-auto">
          <strong className="text-clay"><Tx>Spiritual Notice:</Tx></strong> <Tx>These recordings are authentic Ruqyah Shar'iyyah (Quran-based) — for personal listening with sincere du'a. They are not a substitute for medical diagnosis. For complex spiritual or medical conditions, please book a session at our clinic where a qualified Raqi can guide you in person.</Tx>
        </p>
      </div>
    </div>
  );
};

export default Ruqyah;
